/**
 * Layer control panel — category-grouped checkboxes for toggling map data layers.
 * Collapsible category sections with count badges and category-level toggles.
 */

import { appStore } from '@/core/store';
import { eventBus } from '@/core/events';
import { getLayersByCategory, type LayerCategory, type LayerDef } from '@/config/layers';
import { getSourceById } from '@/config/sources';

/** Color dots per layer for visual identification. */
const LAYER_COLORS: Record<string, string> = {
  'conflicts': '#ef4444',
  'military-bases': '#ef4444',
  'nuclear': '#f59e0b',
  'protests': '#f97316',
  'sanctions': '#a855f7',
  'refugee-camps': '#f97316',
  'embassies': '#6b7280',
  'humanitarian-ops': '#22c55e',
  'fires': '#ef4444',
  'earthquakes': '#22c55e',
  'gdacs': '#f59e0b',
  'volcanoes': '#ef4444',
  'floods': '#06b6d4',
  'cyclones': '#a855f7',
  'vessels': '#06b6d4',
  'naval': '#ef4444',
  'flights': '#a855f7',
  'airports': '#6b7280',
  'cyber-incidents': '#ef4444',
  'undersea-cables': '#06b6d4',
  'pipelines-oil': '#f97316',
  'pipelines-gas': '#22c55e',
  'borders': '#6b7280',
};

/** Display labels for each category. */
const CATEGORY_LABELS: Record<LayerCategory, string> = {
  conflicts: 'CONFLICTS & SECURITY',
  infrastructure: 'INFRASTRUCTURE',
  environmental: 'ENVIRONMENTAL',
  maritime: 'MARITIME',
  aviation: 'AVIATION',
  economic: 'ECONOMIC',
  borders: 'BORDERS',
};

/** Ordered categories for display. */
const CATEGORY_ORDER: LayerCategory[] = [
  'conflicts',
  'infrastructure',
  'environmental',
  'maritime',
  'aviation',
  'economic',
  'borders',
];

/** Check whether a layer's source requires an API key that isn't configured. */
function layerNeedsApiKey(layer: LayerDef): boolean {
  if (!layer.sourceId) return false;
  const source = getSourceById(layer.sourceId);
  if (!source || !source.requiresApiKey) return false;
  // Check if the env key is set (Vite injects env vars at build time)
  if (source.apiKeyEnv) {
    const val = (import.meta as unknown as Record<string, Record<string, string>>).env?.[source.apiKeyEnv];
    if (val && val.length > 0) return false;
  }
  return true;
}

export class LayerControlPanel {
  private container: HTMLElement;
  private categoryElements = new Map<LayerCategory, {
    header: HTMLElement;
    badge: HTMLElement;
    body: HTMLElement;
    checkbox: HTMLInputElement;
  }>();
  private layerCheckboxes = new Map<string, HTMLInputElement>();
  private unsubscribe: (() => void) | null = null;

  constructor() {
    this.container = document.createElement('div');
    this.container.className = 'layer-control';
    this.container.style.cssText = 'display: flex; flex-direction: column;';

    this.buildCategories();
    this.syncFromStore();

    // Subscribe to activeLayers changes for external sync (e.g. variant switch)
    this.unsubscribe = appStore.subscribe(
      (s) => s.activeLayers,
      () => this.syncFromStore(),
    );
  }

  getElement(): HTMLElement {
    return this.container;
  }

  destroy(): void {
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }
  }

  /** Build the full category list with layer rows. */
  private buildCategories(): void {
    const grouped = getLayersByCategory();

    for (const category of CATEGORY_ORDER) {
      const layers = grouped.get(category);
      if (!layers || layers.length === 0) continue;

      const section = document.createElement('div');
      section.className = 'layer-category';

      // Category header
      const header = document.createElement('div');
      header.className = 'layer-category-header';
      header.style.cssText = `
        display: flex; align-items: center; gap: 6px;
        padding: 8px 12px; cursor: pointer; user-select: none;
        font-size: 11px; font-weight: 700; letter-spacing: 0.05em;
        color: var(--text-secondary); border-bottom: 1px solid var(--border-primary);
      `;

      // Category-level custom checkbox
      const catCheckbox = document.createElement('input');
      catCheckbox.type = 'checkbox';
      catCheckbox.className = 'layer-checkbox-native';
      catCheckbox.style.cssText = 'position: absolute; opacity: 0; width: 0; height: 0; pointer-events: none;';

      const catCustomCheck = document.createElement('span');
      catCustomCheck.className = 'layer-cat-checkbox';
      catCustomCheck.style.cssText = `
        width: 10px; height: 10px; flex-shrink: 0;
        border: 1px solid var(--border-primary); border-radius: 2px;
        background: transparent; transition: all 0.12s; cursor: pointer;
      `;
      catCustomCheck.onclick = (e) => {
        e.stopPropagation();
        catCheckbox.checked = !catCheckbox.checked;
        this.toggleCategory(category, catCheckbox.checked);
      };
      header.appendChild(catCheckbox);
      header.appendChild(catCustomCheck);

      // Expand/collapse arrow
      const arrow = document.createElement('span');
      arrow.className = 'layer-category-arrow';
      arrow.textContent = '\u25BE'; // down arrow
      arrow.style.cssText = 'font-size: 10px; transition: transform 0.2s;';
      header.appendChild(arrow);

      // Category label
      const label = document.createElement('span');
      label.style.cssText = 'flex: 1;';
      label.textContent = CATEGORY_LABELS[category] ?? category.toUpperCase();
      header.appendChild(label);

      // Count badge
      const badge = document.createElement('span');
      badge.className = 'layer-category-badge';
      badge.style.cssText = `
        font-size: 10px; font-weight: 600; color: var(--text-muted);
        background: var(--bg-secondary); padding: 1px 6px; border-radius: 8px;
      `;
      header.appendChild(badge);

      // Click header to expand/collapse
      header.setAttribute('role', 'button');
      header.setAttribute('tabindex', '0');
      header.setAttribute('aria-expanded', 'true');
      const toggleCollapse = () => {
        const isCollapsed = body.style.display === 'none';
        body.style.display = isCollapsed ? 'block' : 'none';
        arrow.style.transform = isCollapsed ? '' : 'rotate(-90deg)';
        header.setAttribute('aria-expanded', isCollapsed ? 'true' : 'false');
      };
      header.onclick = toggleCollapse;
      header.onkeydown = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggleCollapse();
        }
      };

      section.appendChild(header);

      // Category body (layer rows)
      const body = document.createElement('div');
      body.className = 'layer-category-body';

      for (const layer of layers) {
        body.appendChild(this.createLayerRow(layer));
      }

      section.appendChild(body);
      this.container.appendChild(section);

      this.categoryElements.set(category, { header, badge, body, checkbox: catCheckbox });
    }
  }

  /** Create a single layer row with custom checkbox and label. */
  private createLayerRow(layer: LayerDef): HTMLElement {
    const row = document.createElement('label');
    row.className = 'layer-row';
    row.style.cssText = `
      display: flex; align-items: center; gap: 6px;
      padding: 3px 8px 3px 22px; cursor: pointer;
      font-size: 10px; color: var(--text-primary);
      text-transform: uppercase; letter-spacing: 0.3px;
    `;

    const needsKey = layerNeedsApiKey(layer);

    // Hidden native checkbox for state management
    const checkbox = document.createElement('input');
    checkbox.type = 'checkbox';
    checkbox.className = 'layer-checkbox-native';
    checkbox.style.cssText = 'position: absolute; opacity: 0; width: 0; height: 0; pointer-events: none;';
    checkbox.dataset.layerId = layer.id;

    // Custom checkbox visual
    const color = LAYER_COLORS[layer.id] ?? '#6b7280';
    const customCheck = document.createElement('span');
    customCheck.className = 'layer-checkbox';
    customCheck.style.cssText = `
      width: 10px; height: 10px; flex-shrink: 0;
      border: 1px solid var(--border-primary); border-radius: 2px;
      background: transparent; transition: all 0.12s;
      display: flex; align-items: center; justify-content: center;
    `;
    customCheck.dataset.color = color;

    if (needsKey) {
      checkbox.disabled = true;
      row.style.opacity = '0.45';
      row.style.cursor = 'default';
    }

    // Sync custom visual with checkbox state
    const syncVisual = () => {
      if (checkbox.checked) {
        customCheck.style.background = color;
        customCheck.style.borderColor = color;
      } else {
        customCheck.style.background = 'transparent';
        customCheck.style.borderColor = 'var(--border-primary)';
      }
    };

    checkbox.onchange = () => {
      if (needsKey) return;
      syncVisual();
      this.toggleLayer(layer.id, checkbox.checked);
    };

    row.appendChild(checkbox);
    row.appendChild(customCheck);

    const nameSpan = document.createElement('span');
    nameSpan.style.cssText = 'flex: 1;';
    nameSpan.textContent = layer.name;
    row.appendChild(nameSpan);

    // Store sync callback so we can update visuals from syncFromStore
    (checkbox as HTMLInputElement & { __syncVisual?: () => void }).__syncVisual = syncVisual;

    if (needsKey) {
      const keyLabel = document.createElement('span');
      keyLabel.style.cssText = 'font-size: 10px; color: var(--text-muted); font-style: italic;';
      keyLabel.textContent = '(key required)';
      row.appendChild(keyLabel);
    }

    this.layerCheckboxes.set(layer.id, checkbox);

    return row;
  }

  /** Toggle a single layer on/off in the store. */
  private toggleLayer(layerId: string, enabled: boolean): void {
    const current = appStore.getState().activeLayers;
    let updated: string[];

    if (enabled) {
      if (current.includes(layerId)) return;
      updated = [...current, layerId];
    } else {
      updated = current.filter((id) => id !== layerId);
    }

    appStore.setState({ activeLayers: updated });
    eventBus.emit('layer:toggled', { layerId, visible: enabled });
  }

  /** Toggle all layers in a category on/off. */
  private toggleCategory(category: LayerCategory, enabled: boolean): void {
    const grouped = getLayersByCategory();
    const layers = grouped.get(category);
    if (!layers) return;

    const current = new Set(appStore.getState().activeLayers);

    for (const layer of layers) {
      if (layerNeedsApiKey(layer)) continue;
      if (enabled) {
        current.add(layer.id);
      } else {
        current.delete(layer.id);
      }
    }

    const updated = Array.from(current);
    appStore.setState({ activeLayers: updated });

    // Emit for each toggled layer
    for (const layer of layers) {
      if (!layerNeedsApiKey(layer)) {
        eventBus.emit('layer:toggled', { layerId: layer.id, visible: enabled });
      }
    }
  }

  /** Sync all checkbox states and badges from the current store state. */
  private syncFromStore(): void {
    const activeSet = new Set(appStore.getState().activeLayers);

    // Update individual layer checkboxes and their custom visuals
    for (const [layerId, checkbox] of this.layerCheckboxes) {
      checkbox.checked = activeSet.has(layerId);
      const syncFn = (checkbox as HTMLInputElement & { __syncVisual?: () => void }).__syncVisual;
      if (syncFn) syncFn();
    }

    // Update category badges and checkbox states
    const grouped = getLayersByCategory();
    for (const [category, elements] of this.categoryElements) {
      const layers = grouped.get(category);
      if (!layers) continue;

      const enableable = layers.filter((l) => !layerNeedsApiKey(l));
      const activeCount = layers.filter((l) => activeSet.has(l.id)).length;
      const totalCount = layers.length;

      elements.badge.textContent = `${activeCount}/${totalCount}`;

      // Category checkbox: checked if all enableable are active, indeterminate if some
      const catCustom = elements.header.querySelector<HTMLElement>('.layer-cat-checkbox');
      if (enableable.length === 0) {
        elements.checkbox.checked = false;
        elements.checkbox.disabled = true;
        if (catCustom) {
          catCustom.style.opacity = '0.4';
          catCustom.style.background = 'transparent';
          catCustom.style.borderColor = 'var(--border-primary)';
        }
      } else {
        const enableableActiveCount = enableable.filter((l) => activeSet.has(l.id)).length;
        elements.checkbox.checked = enableableActiveCount === enableable.length;
        elements.checkbox.indeterminate = enableableActiveCount > 0 && enableableActiveCount < enableable.length;
        elements.checkbox.disabled = false;
        if (catCustom) {
          catCustom.style.opacity = '1';
          if (elements.checkbox.checked) {
            catCustom.style.background = 'var(--accent)';
            catCustom.style.borderColor = 'var(--accent)';
          } else if (elements.checkbox.indeterminate) {
            catCustom.style.background = 'var(--accent-bg)';
            catCustom.style.borderColor = 'var(--accent-border)';
          } else {
            catCustom.style.background = 'transparent';
            catCustom.style.borderColor = 'var(--border-primary)';
          }
        }
      }
    }
  }
}

/** Factory: create the layer control panel element. */
export function createLayerControlPanel(): HTMLElement {
  const panel = new LayerControlPanel();
  const el = panel.getElement();
  (el as HTMLElement & { destroy?: () => void }).destroy = () => panel.destroy();
  return el;
}
